import React, { useState, useEffect, useCallback } from "react";
import { getRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";
import { HelpTitle } from "../AdminHeaderHelp";
import { withCountry } from "../../utils/currencyCountry";
import { backingTone } from "../PoolProtection";

// Internal proof of reserve: USDb in circulation against the USDC that backs it,
// read by lp-api from Hedera (cached 60s). Not public.
// Styles are inline: the admin page loads this pack's JS but not its CSS.

const card = {
  background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "8px",
  padding: "20px 24px", marginBottom: "20px",
};
const h2 = { fontSize: "16px", margin: "0 0 6px" };
const note = { fontSize: "13px", color: colors.gray600, margin: "0 0 12px" };
// Header cells stay in view while the table scrolls inside its box.
const th = {
  position: "sticky", top: 0, zIndex: 1, background: colors.white,
  textAlign: "left", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.4px",
  color: colors.gray600, padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap",
};
const thNum = { ...th, textAlign: "right" };
const td = { padding: "8px 10px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
const thNo = { ...th, width: "1%", textAlign: "right" };
const tdNo = (td) => ({ ...td, width: "1%", textAlign: "right", color: colors.gray600 });
const tdNum = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const mono = { fontFamily: "monospace", fontSize: "12px" };
// The scroll box for a table, so its sticky header has something to stick to.
const tableBox = { overflow: "auto", maxHeight: "70vh" };
const ghostBtn = (disabled) => ({
  background: "#fff", color: colors.primary, border: `1px solid ${colors.primary}`, borderRadius: "6px",
  padding: "8px 16px", fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const TONES = {
  good: { bg: "#d4edda", fg: "#155724" },
  warn: { bg: "#fff3cd", fg: "#856404" },
  bad: { bg: "#f8d7da", fg: "#721c24" },
  info: { bg: "#d1ecf1", fg: "#0c5460" },
  neutral: { bg: "#eceef2", fg: "#4a5060" },
};
const Tag = ({ tone, children }) => (
  <span style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 700, whiteSpace: "nowrap" }}>
    {children}
  </span>
);
const Banner = ({ tone, children }) => (
  <div role="status" style={{ background: TONES[tone].bg, color: TONES[tone].fg, borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "14px" }}>
    {children}
  </div>
);

// Wallets we know by name, so nobody reads them as customer money.
const KNOWN_WALLETS = { "0.0.10128182": "Payout wallet" };

// Older lp-api has no pool status, only the gap: over 25% either way is a
// problem, over 5% a warning.
const GAP_PROBLEM = 0.25;
const GAP_WARNING = 0.05;
export const gapLevel = (gap) => {
  if (gap == null) return null;
  const g = Math.abs(gap);
  return g > GAP_PROBLEM ? "problem" : g > GAP_WARNING ? "warning" : null;
};

// Deposit-only pools (guide 4A.3): only a shortfall is a risk. lp-api sends
// short (under 50%, the pool pauses), low (under 100%), ok, surplus (over
// 110%, swept to fees later) or empty. Surplus is extra cover, so it gets a
// grey tag, and ok and empty get none.
export const POOL_STATUS = {
  short: { tone: "bad", text: "Short" },
  low: { tone: "warn", text: "Low" },
  surplus: { tone: "neutral", text: "Surplus" },
};
const LEGACY = { problem: { tone: "bad", text: "Problem" }, warning: { tone: "warn", text: "Warning" } };
export const poolTag = (p) => (p.status ? POOL_STATUS[p.status] || null : LEGACY[gapLevel(p.gap)] || null);

// Same rule as Pool Protection: next to no USDb and nothing owed to customers.
export const notUsed = (p) => num(p.usdbAllocation) < 0.005 && num(p.fairAllocation) < 0.01;

// "Active tokens only" is remembered per viewer. Storage can be missing or
// blocked; the box then starts on and still works for this visit.
const ACTIVE_ONLY_KEY = "proofOfReserve.activeOnly";
const readActiveOnly = () => {
  try {
    return window.localStorage.getItem(ACTIVE_ONLY_KEY) !== "0";
  } catch (e) {
    return true;
  }
};
const saveActiveOnly = (on) => {
  try {
    window.localStorage.setItem(ACTIVE_ONLY_KEY, on ? "1" : "0");
  } catch (e) {
    // Not remembered; the box still works for this visit.
  }
};

const STATUS_ORDER = { short: 0, low: 1, surplus: 2 };
const absGap = (p) => (p.gap == null ? -1 : Math.abs(p.gap));
// Short, then low (least backed first), then surplus, then the rest. Without a
// status from lp-api: biggest gap first, pools without a fair allocation last.
export const sortPools = (pools) =>
  [...(pools || [])].sort((a, b) => {
    if (!a.status && !b.status) return absGap(b) - absGap(a);
    const ra = STATUS_ORDER[a.status] ?? 3;
    const rb = STATUS_ORDER[b.status] ?? 3;
    if (ra !== rb) return ra - rb;
    if (ra < 2 && a.backing != null && b.backing != null) return a.backing - b.backing;
    return absGap(b) - absGap(a);
  });

// The pool table headers after Circulating, with what their "?" says (lp-api's
// proof-of-reserve service and the signing server's fee formula).
const POOL_COLUMNS = [
  ["Mid rate", "Halfway between the buy and sell rate."],
  ["USDb allocation", "USDb this pool holds, the same figure as In pool on Pool Protection."],
  ["Fair allocation", "Circulating ÷ mid rate: what customers' tokens are worth in USDb."],
  ["Backing", "USDb allocation ÷ fair allocation. A pool on the ledger should read 100%; outside 99.5% to 100.5% it shows red. For other pools, short is under 50% and pauses the pool, low is under 100%, surplus is over 110%."],
  ["Gap", "How far the USDb allocation is above or below the fair allocation, as a share of the fair allocation."],
  ["Dynamic fee", "The base fee, raised the further the pool's price is from the rate. USD-backed pools keep the base fee."],
];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const STALE_HELP = "Yes when the signing server couldn't read this token from Hedera and served stored values.";

const amount = (v, max = 6) => num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: max });
const pct = (v, signed = false) => `${signed && v > 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;
// Fees like 0.75% must not round to 0.8%.
const feePct = (v) => `${Number((num(v) * 100).toFixed(4))}%`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
const hashscan = (network, kind, id) => `https://hashscan.io/${network === "testnet" ? "testnet" : "mainnet"}/${kind}/${id}`;
const Ext = ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;

const ProofOfReserve = ({ figuresUrl, reconciliationUrl }) => {
  const [data, setData] = useState(null);
  const [problem, setProblem] = useState(null); // "notReady" | "error"
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(readActiveOnly);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(figuresUrl);
      if (res?.success) {
        setData(res.data);
        setProblem(null);
      } else {
        setProblem(res?.notReady ? "notReady" : "error");
      }
    } catch (e) {
      setProblem("error");
    } finally {
      setLoading(false);
    }
  }, [figuresUrl]);

  useEffect(() => { load(); }, [load]);

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
      <div>
        <p style={{ ...note, margin: 0 }}>USDb in circulation compared with the USDC reserve, read from Hedera. Internal use only.</p>
      </div>
      <div style={{ textAlign: "right" }}>
        <button type="button" style={ghostBtn(loading)} disabled={loading} onClick={load}>{loading ? "Refreshing…" : "Refresh"}</button>
        {data && <div style={{ fontSize: "12px", color: colors.gray600, marginTop: "6px" }}>As of {when(data.asOf)}</div>}
        <div style={{ fontSize: "12px", color: colors.gray600 }}>lp-api keeps these figures for up to 60 seconds.</div>
      </div>
    </div>
  );

  if (!data) {
    return (
      <div>
        {header}
        {loading ? <p style={note}>Loading…</p>
          : problem === "notReady" ? <Banner tone="info">No figures yet. lp-api hasn't read Hedera since it started. Refresh in a minute.</Banner>
            : <Banner tone="bad">Couldn't load the figures. Refresh to try again.</Banner>}
      </div>
    );
  }

  const { network, usdb = {}, reserve = {}, coverage = {}, usdbHolders = {}, pools = [], lastReconciliation: recon } = data;
  const supply = num(usdb.totalSupply);
  const share = (v) => (supply > 0 ? pct(num(v) / supply) : "-");
  const holders = [
    { label: "Customers", accountId: null, amount: usdbHolders.customers, customer: true },
    ...(usdbHolders.treasury ? [{ label: "Treasury", ...usdbHolders.treasury }] : []),
    ...(usdbHolders.internal || []).map((h) => ({ label: KNOWN_WALLETS[h.accountId] || "Internal wallet", ...h })),
  ];
  const all = sortPools(pools);
  const inUse = all.filter((p) => !notUsed(p)).length;
  const sorted = activeOnly ? all.filter((p) => !notUsed(p)) : all;
  const hasStatus = sorted.some((p) => p.status);
  const offLedger = sorted.filter((p) => !p.onLedger);
  const count = (status) => offLedger.filter((p) => p.status === status).length;
  const onLedger = sorted.filter((p) => p.onLedger && !notUsed(p));
  const offBand = onLedger.filter((p) => backingTone(p.backing, true) === "bad").length;
  const problems = sorted.filter((p) => gapLevel(p.gap) === "problem").length;
  const warnings = sorted.filter((p) => gapLevel(p.gap) === "warning").length;

  const coverageLine = coverage.ratio == null
    ? <p style={{ margin: 0 }}>No USDb in circulation, so there's nothing to cover.</p>
    : coverage.covered
      ? <p style={{ margin: 0 }}><Tag tone="good">Covered</Tag> The reserve holds {num(coverage.ratio).toFixed(2)}× the USDb in circulation, {amount(coverage.surplus)} USDC more than it needs.</p>
      : <p style={{ margin: 0 }}><Tag tone="bad">Not covered</Tag> The reserve is {amount(Math.abs(num(coverage.surplus)))} USDC short of the USDb in circulation.</p>;

  return (
    <div>
      {header}
      {problem === "error" && <Banner tone="bad">Couldn't load the figures. Refresh to try again.</Banner>}
      {data.stale && <Banner tone="warn">lp-api couldn't read Hedera just now. These are the last figures it got, as of {when(data.asOf)}.</Banner>}

      <div style={card}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "16px" }}>
          <div>
            <div style={note}>USDb in circulation</div>
            <div style={{ fontSize: "28px", fontWeight: 700 }}>{amount(usdb.totalSupply)} USDb</div>
            {usdb.tokenId && <div style={{ fontSize: "12px", marginTop: "4px" }}><Ext href={hashscan(network, "token", usdb.tokenId)}>USDb token on HashScan</Ext> <span style={mono}>{usdb.tokenId}</span></div>}
          </div>
          <div>
            <div style={note}>USDC in reserve</div>
            <div style={{ fontSize: "28px", fontWeight: 700 }}>{amount(reserve.total)} USDC</div>
          </div>
        </div>
        <div style={{ fontSize: "14px" }}>{coverageLine}</div>
      </div>

      <div style={card}>
        <h2 style={h2}>Who holds the USDb</h2>
        <p style={note}>Only the Customers row is customer funds. Treasury and internal wallets are company funds.</p>
        <div style={tableBox}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thNo}>#</th><th style={th}>Holder</th><th style={th}>Account</th><th style={thNum}>USDb</th><th style={thNum}>Share of supply</th></tr></thead>
            <tbody>
              {holders.map((h, i) => (
                <tr key={h.accountId || `row${i}`}>
                  <td style={tdNo(td)}>{i + 1}</td>
                  <td style={{ ...td, fontWeight: h.customer ? 700 : 400 }}>{h.label}</td>
                  <td style={td}>{h.accountId ? <Ext href={hashscan(network, "account", h.accountId)}><span style={mono}>{h.accountId}</span></Ext> : "-"}</td>
                  <td style={tdNum}>{amount(h.amount)}</td>
                  <td style={tdNum}>{share(h.amount)}</td>
                </tr>
              ))}
              <tr>
                <td style={td} /><td style={{ ...td, fontWeight: 700 }}>Total</td><td style={td} />
                <td style={{ ...tdNum, fontWeight: 700 }}>{amount(usdb.totalSupply)}</td><td style={td} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={card}>
        <h2 style={h2}>Reserve accounts</h2>
        <p style={note}>Each account links to HashScan so you can check the balance on Hedera.</p>
        <div style={tableBox}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={thNo}>#</th><th style={th}>Account</th><th style={thNum}>USDC</th></tr></thead>
            <tbody>
              {(reserve.accounts || []).map((a, i) => (
                <tr key={a.accountId}>
                  <td style={tdNo(td)}>{i + 1}</td>
                  <td style={td}><Ext href={hashscan(network, "account", a.accountId)}><span style={mono}>{a.accountId}</span></Ext></td>
                  <td style={tdNum}>{amount(a.usdc)}</td>
                </tr>
              ))}
              <tr><td style={td} /><td style={{ ...td, fontWeight: 700 }}>Total</td><td style={{ ...tdNum, fontWeight: 700 }}>{amount(reserve.total)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={card}>
        <h2 style={h2}>Local currency pools</h2>
        <p style={note}>
          {hasStatus
            ? `${plural(onLedger.length, "pool")} on the ledger, ${offBand} outside 99.5% to 100.5%. ${count("short")} short and ${count("low")} low among the other ${plural(offLedger.length, "pool")}: short is under 50% backed and pauses the pool, low is under 100%.`
            : `${plural(problems, "problem")} and ${plural(warnings, "warning")} across ${plural(sorted.length, "pool")}. A gap over 25% is a problem, over 5% a warning.`}
        </p>
        <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", marginBottom: "10px", fontSize: "13px" }}>
          <label style={{ display: "flex", gap: "6px", alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); saveActiveOnly(e.target.checked); }} />
            Active tokens only
          </label>
          <span style={{ color: colors.gray600 }}>
            {activeOnly ? `Showing ${sorted.length} of ${inUse} in use` : `Showing ${sorted.length} of ${all.length} · ${inUse} in use`}
          </span>
        </div>
        <div style={tableBox}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thNo}>#</th><th style={th}>Currency</th><th style={thNum}>Circulating</th>
                {POOL_COLUMNS.map(([title, help]) => <th key={title} style={thNum}><HelpTitle title={title} help={help} /></th>)}
                <th style={th}><HelpTitle title="Stale" help={STALE_HELP} /></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => {
                const ledgerTone = p.onLedger && !notUsed(p) ? backingTone(p.backing, true) : null;
                const tag = p.onLedger ? null : poolTag(p);
                return (
                  <tr key={p.tokenId || p.symbol}>
                    <td style={tdNo(td)}>{i + 1}</td>
                    <td style={td}>
                      <strong>{withCountry(p.symbol)}</strong>
                      {p.tokenId && <div style={{ ...mono, color: colors.gray600 }}>{p.tokenId}</div>}
                    </td>
                    <td style={tdNum}>{amount(p.circulating, 2)}</td>
                    <td style={tdNum}>{p.midRate == null ? "-" : amount(p.midRate)}</td>
                    <td style={tdNum}>{amount(p.usdbAllocation)}</td>
                    <td style={tdNum}>{p.fairAllocation == null ? "-" : amount(p.fairAllocation)}</td>
                    <td style={tdNum}>
                      {p.backing == null ? "-" : ledgerTone ? <Tag tone={ledgerTone}>{pct(p.backing)}</Tag> : pct(p.backing)}
                      {tag && <> <Tag tone={tag.tone}>{tag.text}</Tag></>}
                    </td>
                    <td style={tdNum}>{p.gap == null ? "-" : pct(p.gap, true)}</td>
                    <td style={tdNum}>{feePct(p.dynamicFee)}</td>
                    <td style={td}>{p.stale ? <Tag tone="warn">Yes</Tag> : "No"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={card}>
        <h2 style={h2}>Last reconciliation</h2>
        {!recon ? <p style={{ ...note, margin: 0 }}>No reconciliation has run yet.</p> : (
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "baseline", fontSize: "14px" }}>
            <div><span style={note}>Status </span>
              <Tag tone={recon.status === "pass" ? "good" : "bad"}>{{ pass: "Pass", fail: "Fail", error: "Error" }[recon.status] || recon.status}</Tag>
            </div>
            <div><span style={note}>Finished </span>{recon.finishedAt ? when(recon.finishedAt) : "Still running"}</div>
            <div><span style={note}>Failing checks </span><strong>{num(recon.failing)}</strong></div>
            {reconciliationUrl && <a href={reconciliationUrl}>Open it on the Ledger page</a>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProofOfReserve;
